import _ from 'underscore';
import { useCallback, useMemo } from 'react';

import { McaAgeTargets, McaChannelTypeBudgetRatio, McaChannelTypes, McaDefaultAgeTargets, McaDefaultGenderTargets, McaGenderTargets, McaTargetModel } from '@bingads-webui-campaign-react/mca-core';
import { useAudienceKPIEstimationV2 } from '@bingads-webui/overreact-ad-insight';
import { CURRENCY } from '@bingads-webui-multi-channel/locale-and-market-context';

import { useAccountLevelVariables, usePostponeReadVariables } from './use-variables';

export const DEFAULT_BID = 1;
export const DEFAULT_CAMPAIGN_SUBTYPE = 0; // StaticAds
export const DEFAULT_CAMPAIGN_BID_STRATEGY = 1; // Manual

// AdInsight has a different mapping for some currencies
export const AdInsightCurrency = {
  ...CURRENCY,
  eur: 'EURO',
  idr: 'Rupiah',
  myr: 'MalaysianRinggit',
  pen: 'NuevoSol',
  qar: 'QatariRial',
  thb: 'Baht',
  usd: 'USDollar',
  vef: 'Bolivar',
  vnd: 'Dong',
};

const getAdInsightCurrency = currency => _.get(AdInsightCurrency, currency.toLowerCase());

const AGE_TARGETS = _.map(McaDefaultAgeTargets, ageRange => ({
  '@odata.type': McaTargetModel.AgeTarget, AgeRange: ageRange,
}));

const GENDER_TARGETS = _.map(McaDefaultGenderTargets, genderType => ({
  '@odata.type': McaTargetModel.GenderTarget, GenderType: genderType,
}));

const groupTargetsByType = targets => _.groupBy(targets, target => target['@odata.type']);

const isTargetIncluded = (target, targets) => _.where(targets, target).length > 0;

// All targets in `targetList` are considered as included, the rest are excluded.
// But, if the list doesn't have any age or gender targets, then all of them are included respectively.
export const getIncludedTargets = (targets) => {
  let targetsWithDefaults = targets;

  const hasAgeTargets = isTargetIncluded({ '@odata.type': McaTargetModel.AgeTarget }, targets);

  if (!hasAgeTargets) {
    targetsWithDefaults = _.union(targetsWithDefaults, AGE_TARGETS);
  }

  const hasGenderTargets = isTargetIncluded({ '@odata.type': McaTargetModel.GenderTarget }, targets);

  if (!hasGenderTargets) {
    targetsWithDefaults = _.union(targetsWithDefaults, GENDER_TARGETS);
  }

  return targetsWithDefaults;
};

export const getAudienceBudgetSuggestionPayload = (budget, targets = []) => {
  const { dailyBudget, currency } = budget;

  const includedTargets = getIncludedTargets(targets);

  const {
    [McaTargetModel.AgeTarget]: ageTargets,
    [McaTargetModel.GenderTarget]: genderTargets,
    [McaTargetModel.LocationTarget]: locationTargets,
    [McaTargetModel.RadiusTarget]: radiusTargets,
  } = groupTargetsByType(includedTargets);

  return {
    request: {
      DailyBudget: dailyBudget,
      Currency: getAdInsightCurrency(currency),
      CampaignSubType: DEFAULT_CAMPAIGN_SUBTYPE,
      Bid: DEFAULT_BID,
      CampaignBiddingStrategy: DEFAULT_CAMPAIGN_BID_STRATEGY,

      Age: {
        Includes: _.filter(McaAgeTargets, target => isTargetIncluded({ AgeRange: target }, ageTargets)),
        Excludes: _.filter(McaAgeTargets, target => !isTargetIncluded({ AgeRange: target }, ageTargets)),
      },
      Gender: {
        Includes: _.filter(McaGenderTargets, target => isTargetIncluded({ GenderType: target }, genderTargets)),
        Excludes: _.filter(McaGenderTargets, target => !isTargetIncluded({ GenderType: target }, genderTargets)),
      },
      Location: {
        Includes: _.map(locationTargets, target => target.BingLocationId),
        Excludes: [],
      },
      RadiusTargets: _.map(radiusTargets, target => ({
        Radius: target.RadiusLength,
        CenterLatitude: target.Latitude,
        CenterLongitude: target.Longitude,
      })),
    },
  };
};

export const getAudienceBudgetRatio = (campaign) => {
  const servingChannels = _.get(campaign, 'ServingChannels', []);

  if (!_.contains(servingChannels, McaChannelTypes.MSAN)) {
    return 0;
  }

  const msanRatio = McaChannelTypeBudgetRatio[McaChannelTypes.MSAN];

  const totalRatios = _.chain(servingChannels)
    .map(channel => McaChannelTypeBudgetRatio[channel])
    .reduce((sum, channelRatio) => (sum + channelRatio))
    .value();

  return (msanRatio / totalRatios);
};

export const useAudienceBudgetSuggestion = () => {
  const variables = useAccountLevelVariables();
  const config = usePostponeReadVariables();

  const [{ data, errors }, { refetch }] = useAudienceKPIEstimationV2(variables, config);

  const read = useCallback((budget, targets) => {
    refetch({ payload: getAudienceBudgetSuggestionPayload(budget, targets) });
  }, [refetch]);

  const debouncedRead = useMemo(() => _.debounce(read, 500), [read]);

  return [{ data, errors }, { read: debouncedRead }];
};
